import { useFormField } from '@/components/ui/form';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/lib/utils';

interface TranslatedFormMessageProps {
  className?: string;
}

/**
 * FormMessage that translates error messages (which are i18n keys from Zod validation).
 *
 * Use this component instead of FormMessage when your Zod validation schema
 * returns translation keys as error messages.
 *
 * @example
 * ```tsx
 * <FormField
 *   control={control}
 *   name="email"
 *   render={({ field }) => (
 *     <FormItem>
 *       <FormLabel>Email</FormLabel>
 *       <FormControl>
 *         <Input {...field} />
 *       </FormControl>
 *       <TranslatedFormMessage />
 *     </FormItem>
 *   )}
 * />
 * ```
 */
export function TranslatedFormMessage({
  className,
}: TranslatedFormMessageProps) {
  const { error } = useFormField();
  const { t } = useTranslation();

  if (!error?.message) return null;

  const translatedMessage = t(error.message, { defaultValue: error.message });

  return (
    <p className={cn('text-destructive text-sm', className)}>
      {translatedMessage}
    </p>
  );
}
